import { Switch } from '@mui/material';
import { observer } from 'mobx-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import React from 'react';
import { useQueryClient } from 'react-query';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import SvgIcon from '@/assets/svgIcon';
import { Spinner } from '@/components/Spinner';
import {
  useFetchGroups,
  useFetchMultiChannelGroups,
  usePollGroupUpdates,
} from '@/services/chatService';
import { chatKey } from '@/services/queryKey';
import useToggleStore from '@/store/toggleStore';

import { GroupStatus } from '../constants';
import { useObserver } from '../hooks';
import { useChatStore } from '../store';
import { GroupItem } from './GroupItem';

interface Props {
  computedVenueId: number;
  getVenueIds: () => string | undefined;
}

export const ChatLnb = observer(({ computedVenueId, getVenueIds }: Props) => {
  const store = useChatStore();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isToggled } = useToggleStore();
  const queryClient = useQueryClient();
  const { venueId, clientId } = useParams();
  const $bottom = useRef<HTMLDivElement>(null);
  const [hideClosed, setHideClosed] = useState(false);
  const VIEW_ONLY_UNCLOSED_KEY = 'view_only_unclosed';
  enum VIEW_ONLY_UNCLOSED_VAL {
    TRUE = 'true',
    FALSE = 'false',
  }

  const {
    fetchNextPage: fetchMultipleGroupNextPage,
    hasNextPage: hasMultipleGroupNextPage,
    isFetching: isMultipleGroupFetching,
  } = useFetchMultiChannelGroups(
    {
      size: 20,
      venueIds: getVenueIds(),
    },
    { enabled: !venueId && getVenueIds() !== undefined }
  );

  const {
    fetchNextPage: fetchSingleGroupNextPage,
    hasNextPage: hasSingleGroupNextPage,
    isFetching: isSingleGroupFetching,
  } = useFetchGroups(
    {
      venueId: computedVenueId,
      size: 20,
    },
    { enabled: !!venueId }
  );

  usePollGroupUpdates({
    venueIds: venueId ? String(computedVenueId) : getVenueIds(),
  });

  const fetchNextPage = () => {
    if (venueId) {
      fetchSingleGroupNextPage();
    } else {
      fetchMultipleGroupNextPage();
    }
  };
  const isFetching = venueId ? isSingleGroupFetching : isMultipleGroupFetching;
  const hasNextPage = () => {
    return venueId ? hasSingleGroupNextPage : hasMultipleGroupNextPage;
  };
  const refresh = () => {
    if (venueId) {
      queryClient.resetQueries(chatKey.fetchGroups(computedVenueId));
    } else {
      queryClient.resetQueries(chatKey.fetchMultiChannelGroups(getVenueIds()));
    }
  };

  useEffect(() => {
    if (searchParams.get('groupId')) {
      setTimeout(() => {
        store.setDefaultSelectedGroup(Number(searchParams.get('groupId')));
        searchParams.delete('groupId');
        navigate(
          {
            pathname,
            search: searchParams.toString(),
          },
          {
            replace: true,
          }
        );
      }, 30);
    }
  }, [searchParams]);

  useEffect(() => {
    store.emptyGroupMap();
    store.emptyMessageMap();
    refresh();
  }, [pathname]);

  useLayoutEffect(() => {
    const storedFlag = localStorage.getItem(VIEW_ONLY_UNCLOSED_KEY);
    if (storedFlag && storedFlag === VIEW_ONLY_UNCLOSED_VAL.TRUE)
      setHideClosed(true);
  }, []);

  useEffect(() => {
    localStorage.setItem(VIEW_ONLY_UNCLOSED_KEY, hideClosed.toString());
  }, [hideClosed]);

  useEffect(() => {
    setHideClosed(isToggled);
  }, [isToggled]);

  const groupList = hideClosed
    ? store.groupList
        .filter((group) => group.title.includes(store.searchKeyword))
        .filter((group) => group.status !== GroupStatus.CLOSED)
    : store.groupList.filter((group) =>
        group.title.includes(store.searchKeyword)
      );

  const handleStartNewChat = () => {
    if (
      window.matchMedia('(max-width: 768px)').matches &&
      venueId &&
      clientId
    ) {
      navigate(`/chat/venue/${venueId}/group/-1`); // Redirect to the new chat page
      if (store.newChatStarted) {
        return;
      }
      store.startNewChat();
      return;
    }
    if (store.newChatStarted) {
      return;
    }

    store.startNewChat();
  };

  const toggleChecked = () => {
    setHideClosed(!hideClosed);
    if (store.currentGroup.status === GroupStatus.CLOSED) {
      const targetId = store.groupList.find(
        (group) => group.status !== GroupStatus.CLOSED
      )?.id;
      if (targetId) store.setDefaultSelectedGroup(targetId);
    }
  };

  const onIntersect: IntersectionObserverCallback = ([entry]) => {
    !isFetching && entry.isIntersecting && hasNextPage() && fetchNextPage();
  };

  useObserver({
    target: $bottom,
    onIntersect,
  });

  return (
    <div className="box-border min-w-[352px] flex flex-col  rounded border-[1px] border-[#ececec] px-4 pb-4 pt-4 max-md:border-none max-md:w-full max-md:h-[calc(100vh - 250px)]">
      <div className="h-5 mb-4 mt-6 flex items-center justify-end gap-3 text-sm text-[#1c1c1e] max-md:hidden">
        <Switch
          disabled={store.groupList.length === 0}
          onChange={toggleChecked}
          checked={hideClosed}
        />{' '}
        미해결 문의만 보기
      </div>
      <ul className="flex min-w-full flex-1 flex-col overflow-y-auto rounded border-[1px] border-solid border-gray-200 ">
        {venueId && (
          <button
            onClick={handleStartNewChat}
            className="min-h-20 flex items-center gap-3 pl-4 text-base font-semibold border-b-[1px] border-solid border-gray-200 text-[#555555]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full border-[1px] border-solid border-[#0000000D]">
              <SvgIcon width={24} height={24} name="plusIcon" />
            </div>
            새로운 문의/답변 시작하기
          </button>
        )}
        <React.Suspense fallback={<Spinner />}>
          {groupList.length === 0 && (
            <div className="flex flex-col items-center gap-2 justify-center h-full md:hidden">
              <img
                width={240}
                height={240}
                src={'/image/empty.png'}
                alt={'없습니다.'}
              />
              <p className="font-medium text-xl text-gray-900">
                문의/답변 기록이 없습니다.
              </p>
              <p className="font-normal text-gray-500 text-base">
                원하는 지점을 선택하여 메시지를 전송해보세요.
              </p>
            </div>
          )}
          {groupList.map((group) => (
            <GroupItem group={group} key={group.id} />
          ))}
          <div ref={$bottom} />
        </React.Suspense>
      </ul>
    </div>
  );
});
